import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GenericTableModal } from '../../CompanycodeTranslation/components/CreationModal';
import { TableColumn, TableAction, EditableComponentProps } from '../../CompanycodeTranslation/components/CreationModal/types';
import { HSNRowData } from '../types/HSNRowType';
import { HSNEditableComponent } from './HSNEditableComponent';
import {
  useLazySearchHSNQuery,
} from '../Api/searchHSN.api';
import type { SearchHSNResponse } from '../Api/searchHSN.api';

export interface CreateHSNModalProps {
  open: boolean;
  onClose: () => void;
  codeType?: 'HSN' | 'SAC' | ''; // Filter by code type: 'HSN', 'SAC', or '' for both
  onSelect?: (code: string) => void; // Called after successfully creating a new code
}

export const CreateHSNModal: React.FC<CreateHSNModalProps> = ({
  open,
  onClose,
  codeType = '',
  onSelect,
}) => {
  const PAGE_SIZE = 50;

  // Infinite-scroll fetch state (handled fully inside this modal)
  const isFetchingRef = useRef(false);
  const fetchSessionRef = useRef(0); // bumps to invalidate in-flight requests (e.g., on close / codetype change)
  const [rowFrom, setRowFrom] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Lazy query so we can fetch additional pages on scroll
  const [triggerSearchHSN, { isFetching }] = useLazySearchHSNQuery();

  // Delete mutation

  // Transform API data to HSNRowData format
  const [data, setData] = useState<HSNRowData[]>([]);

  const transformItemsToRows = useCallback(
    (items: SearchHSNResponse, baseIndex = 0): HSNRowData[] => {
      return items.map((item, index) => {
        const companyId = item.companyID ?? item.raw?.companyid ?? 0;
        const isUserCreated = companyId !== 0;
        const derivedId =
          item.hsnID ?? item.raw?.hsnsacid ?? (baseIndex + index + 1);

        return {
          id: Number(derivedId),
          code: item.value || item.raw?.hsnsaccode || '',
          codeType: item.codetype || item.raw?.codetype || 'HSN',
          description: item.description || item.raw?.description || '',
          raw: item.raw,
          isEditable: false,
          // isUserCreated,
          isDeletable: false,
          // isUserCreated,
          isActivetable: true,
          isDeactivatable: true,
        } ;
        // as HSNRowData;
      });
    },
    []
  );

  const fetchPage = useCallback(
    async (nextRowFrom: number, sessionId: number = fetchSessionRef.current) => {
      if (!open || isFetchingRef.current) return;
      isFetchingRef.current = true;

      try {
        const result = await triggerSearchHSN({
          rowfrom: nextRowFrom,
          limitno: PAGE_SIZE,
          word: '',
          codetype: codeType,
        }).unwrap();

        // Ignore responses from previous sessions (e.g., modal closed or codetype changed mid-flight)
        if (!open || sessionId !== fetchSessionRef.current) return;

        const fetchedItems: SearchHSNResponse = result ?? [];
        const newRows = transformItemsToRows(fetchedItems, nextRowFrom);

        setData((prev) => {
          const existingIds = new Set(prev.map((r) => r.id));
          const deduped = newRows.filter((r) => !existingIds.has(r.id));
          return [...prev, ...deduped];
        });

        setRowFrom(nextRowFrom);
        if (fetchedItems.length < PAGE_SIZE) {
          setHasMore(false);
        }
      } catch (e) {
        // If an error occurs, stop further auto-fetches until the user retries (e.g., reopen modal)
        setHasMore(false);
        console.error('Error fetching HSN/SAC page:', e);
      } finally {
        isFetchingRef.current = false;
      }
    },
    [PAGE_SIZE, codeType, open, transformItemsToRows, triggerSearchHSN]
  );

  const resetAndFetchFirstPage = useCallback(() => {
    fetchSessionRef.current += 1;
    const sessionId = fetchSessionRef.current;
    isFetchingRef.current = false;
    setRowFrom(0);
    setHasMore(true);
    setData([]);
    fetchPage(0, sessionId);
  }, [fetchPage]);

  useEffect(() => {
    if (open) {
      resetAndFetchFirstPage();
    } else {
      fetchSessionRef.current += 1; // invalidate any in-flight request
      setData([]);
      setRowFrom(0);
      setHasMore(true);
      isFetchingRef.current = false;
    }
  }, [open, codeType, resetAndFetchFirstPage]);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    const node = event.currentTarget;
    const isBottom =
      node.scrollHeight - node.scrollTop <= node.clientHeight + 10;

    if (isBottom && hasMore && !isFetchingRef.current && !isFetching) {
      fetchPage(rowFrom + PAGE_SIZE, fetchSessionRef.current);
    }
  };

  // Define HSN/SAC-specific headers with strict typing
  const headers: TableColumn<HSNRowData>[] = [
    {
      key: 'id',
      label: 'ID',
      width: 20,
      align: 'center',
      render: (value, row, index) => (index !== undefined ? index + 1 : value),
    },
    { key: 'codeType', label: 'Code Type', width: 120 },
    { key: 'code', label: codeType === 'HSN' ? 'HSN Code' : codeType === 'SAC' ? 'SAC Code' : 'HSN/SAC Code', width: 150 },
    { key: 'description', label: 'Description', width: 300 },
  ];

  // HSN/SAC-specific action handlers with strict typing
  const handleAction = (action: TableAction, row: HSNRowData, rowIndex?: number) => {
    console.log('HSN/SAC Action:', action, 'Row:', row, 'Index:', rowIndex);

    switch (action) {
      case 'onedit':
        break;
      case 'ondelete':
      break;
      case 'oncancel':
        // Cancel action is handled by the table component
        break;
      case 'onactive':
        setData((prev) =>
          prev.map((item) => (item.id === row.id ? { ...item, isActive: true } : item))
        );
        // Add API call here if needed
        break;
      case 'oninactive':
        setData((prev) =>
          prev.map((item) => (item.id === row.id ? { ...item, isActive: false } : item))
        );
        // Add API call here if needed
        break;
      case 'onsave':
        // Save action - check if it's a new row or update
        // Use raw.hsnsacid for matching if available, otherwise use id
        const rowId = row.raw?.hsnsacid || row.id;
        const existingIndex = data.findIndex((item) => {
          const itemId = item.raw?.hsnsacid || item.id;
          return itemId === rowId;
        });

        if (existingIndex !== -1) {
          // Update existing row - preserve edit/delete flags based on companyid
          setData((prev) =>
            prev.map((item) => {
              const itemId = item.raw?.hsnsacid || item.id;
              if (itemId === rowId) {
                const companyId = row.raw?.companyid ?? 0;
                const isUserCreated = companyId !== 0;
                return {
                  ...row,
                  isEditing: false,
                  isEditable: isUserCreated,
                  isDeletable: isUserCreated,
                };
              }
              return item;
            })
          );
        } else {
          const companyId = row.raw?.companyid ?? 0;
          const isUserCreated = companyId !== 0;
          setData((prev) => [
            ...prev,
            {
              ...row,
              isEditing: false,
              isEditable: isUserCreated,
              isDeletable: isUserCreated,
            },
          ]);

          // New row created successfully -> select it in the caller (Item form) and close modal
          if (onSelect && row.code) {
            onSelect(row.code);
            onClose();
            return;
          }
        }
        // Refresh list from API so the table stays consistent (and includes server-calculated values)
        resetAndFetchFirstPage();
        break;
      default:
        break;
    }
  };

  const modalTitle =
    codeType === 'HSN'
      ? 'HSN Code Management'
      : codeType === 'SAC'
      ? 'SAC Code Management'
      : 'HSN/SAC Code Management';

  // Wrapper component to pass codeType to HSNEditableComponent
  const HSNEditableComponentWrapper: React.FC<EditableComponentProps<HSNRowData>> = (props) => {
    return <HSNEditableComponent {...props} codeType={codeType} />;
  };

  return (
    <GenericTableModal<HSNRowData>
      open={open}
      onClose={onClose}
      title={modalTitle}
      headers={headers}
      data={data}
      onAction={handleAction}
      EditableComponent={HSNEditableComponentWrapper}
      onScroll={handleScroll}
    />
  );
};

